import React from 'react';
import { ChevronLeft, ChevronRight, FileText } from 'react-feather';
import RunStatePill, { runState } from '../EtlPages/RunState';

/**
 * Shared File Tracking primitives.
 *
 * The status vocabulary that used to live here (STATUS_CONFIG, StatusPill,
 * StatusIcon) moved to EtlPages/RunState — it was one of three tables
 * describing the same pipeline in three palettes. `StatusPill` stays as a thin
 * alias so the tracking views keep reading naturally.
 *
 * ViewToggle is gone: the six tracking views are tabs of the ETL Configuration
 * shell now, so the page-level tab strip is the only navigation.
 */

/** Alias — the tracking views speak in "status", the kit in "run state". */
export const StatusPill = ({ status, size }) => (
  <RunStatePill status={status} large={size !== 'small'} />
);

/** The tone for a status, when a caller needs to colour something itself. */
export const statusTone = (status) => runState(status).tone;

/** Pagination for the files table. */
export const Pagination = ({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange }) => {
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const pages = React.useMemo(() => {
    const out = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i += 1) out.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i += 1) out.push(i);
      out.push('…', totalPages);
    } else if (currentPage >= totalPages - 2) {
      out.push(1, '…');
      for (let i = totalPages - 3; i <= totalPages; i += 1) out.push(i);
    } else {
      out.push(1, '…', currentPage - 1, currentPage, currentPage + 1, '…', totalPages);
    }
    return out;
  }, [currentPage, totalPages]);

  return (
    <div className="ep-pager">
      <span className="cp-meta">
        {startItem}–{endItem} of {totalItems.toLocaleString()}
      </span>
      <div className="ep-pager-controls">
        <button
          type="button"
          className="cp-btn cp-icon cp-sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft size={14} />
        </button>
        {pages.map((page, idx) => (page === '…' ? (
          <span key={`gap-${idx}`} className="ep-pager-gap">…</span>
        ) : (
          <button
            key={page}
            type="button"
            className={`cp-btn cp-sm ep-pager-n${page === currentPage ? ' cp-active' : ''}`}
            onClick={() => onPageChange(page)}
            aria-current={page === currentPage ? 'page' : undefined}
          >
            {page}
          </button>
        )))}
        <button
          type="button"
          className="cp-btn cp-icon cp-sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          aria-label="Next page"
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
};

/** The feed a record belongs to. */
export const FileTypeBadge = ({ fileType }) => (
  <span className="cp-tag">
    <FileText size={11} />
    <span style={{ marginLeft: 5 }}>{fileType}</span>
  </span>
);

/** Local date + time, matching what the CSV export writes. */
export const formatTimestamp = (isoString) => {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
};

export const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(`${dateString}T00:00:00`).toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
};

/**
 * Counts for the summary tiles. Trusts the backend status field —
 * manualReview date matching happens server-side, so stale flags from earlier
 * dates can't pollute the counts here.
 */
export const calculateStatusCounts = (records) => {
  const counts = { all: records.length, processed: 0, pending: 0, needs_review: 0, missed: 0, failed: 0 };
  for (const record of records) {
    switch (record.status) {
      case 'needs_review': counts.needs_review += 1; break;
      case 'processed': counts.processed += 1; break;
      case 'pending':
      case 'received': counts.pending += 1; break;
      case 'missed': counts.missed += 1; break;
      case 'failed': counts.failed += 1; break;
      default: break;
    }
  }
  return counts;
};

export default {
  StatusPill,
  statusTone,
  Pagination,
  FileTypeBadge,
  formatTimestamp,
  formatDate,
  calculateStatusCounts,
};
